'use client';
import {
  AccountCircle,
  Check,
  Description,
  DoneAll,
  Mail,
  Menu,
  Notifications,
} from '@mui/icons-material';
import {
  AppBar,
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import Badge from '@mui/material/Badge';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CourseTabList } from '@/constants/menu';
import { useAuth, useCourses } from '@/providers';
import { useViewStore } from '@/stores';
import type { Breadcrumb } from '@/types';
import { useRoute } from '@/utils/client/route';
import { getAppDetail } from '@/utils/server/app';

const testMenuHintStorageKey = 'testMenuHintSeen';

export default function BarTop() {
  const { t } = useTranslation();
  const { isRoute, getRoute, goToRoute } = useRoute();
  const { selectedCourse, selectedChapter, isChapterCompleted, isExerciseCorrect } = useCourses();
  const { showChapterNote, showMenuDrawer, toggleChapterNote, toggleMenuDrawer } = useViewStore();
  const { isLoggedIn } = useAuth();
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
  const [viewTitle, setViewTitle] = useState<string>(getAppDetail().title);
  const [showTestMenuHint, setShowTestMenuHint] = useState(false);

  const isTest = selectedCourse?.type === 'Test';

  const onNavigate = (routeId: string, params?: Record<string, string | number>) => {
    goToRoute(routeId, params);
  };

  const dismissTestMenuHint = useCallback(() => {
    setShowTestMenuHint(false);
    if (typeof window !== 'undefined') {
      localStorage.setItem(testMenuHintStorageKey, 'true');
    }
  }, []);

  useEffect(() => {
    if (!isTest || !isRoute('myCourseLearn')) return;
    if (typeof window !== 'undefined' && localStorage.getItem(testMenuHintStorageKey) === 'true') {
      return;
    }

    setShowTestMenuHint(true);
    const timer = window.setTimeout(dismissTestMenuHint, 6000);
    return () => window.clearTimeout(timer);
  }, [dismissTestMenuHint, isRoute, isTest]);

  useEffect(() => {
    if (showMenuDrawer && showTestMenuHint) {
      dismissTestMenuHint();
    }
  }, [dismissTestMenuHint, showMenuDrawer, showTestMenuHint]);

  const handleMenuToggle = () => {
    if (showTestMenuHint) {
      dismissTestMenuHint();
    }
    toggleMenuDrawer();
  };

  const setTitles = useCallback(() => {
    let newTitle: string = getAppDetail().title;
    let newPageTitle = '';
    const newBreadcrumbs = [];
    newBreadcrumbs.push({
      title: 'Meine Kurse',
      routeId: 'myCourses',
    });
    const route = getRoute();
    if (route) {
      if (route.title !== 'selectedCourseTitle' && route.title !== 'selectedChapterTitle') {
        newTitle = route.title;
        newPageTitle = route.title;
      }

      if (selectedCourse) {
        const { title: courseTitle, slug: courseSlug } = selectedCourse;
        const breadcrumbTitle = courseTitle;
        newTitle = courseTitle;
        newPageTitle = courseTitle;

        newBreadcrumbs.push({
          title: breadcrumbTitle,
          routeId: 'myCourseTabs',
          params: { courseSlug, tabId: CourseTabList.inhalte },
        });

        if (selectedChapter) {
          const { id } = selectedChapter;
          const { curriculum } = selectedCourse;
          const chapter = curriculum.find((item) => item.item.id === id);
          if (chapter) {
            const {
              prefix,
              style,
              item: { slug: chapterSlug },
            } = chapter;
            newTitle =
              style !== 'item' ? selectedChapter.title : `Kapitel${prefix ? `: ${prefix}` : ''}`;
            newPageTitle = selectedChapter.title;
            newBreadcrumbs.push({
              title: newTitle,
              routeId: 'myCourseLearn',
              params: { courseSlug, chapterSlug },
            });
          }
        }
      }
    }
    setBreadcrumbs(newBreadcrumbs);
    setViewTitle(newPageTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChapter, selectedCourse, getRoute]);

  useEffect(() => {
    setTitles();
  }, [setTitles]);

  return (
    <AppBar
      color="default"
      position="sticky"
      sx={{
        width: '100%',
        boxShadow: '0 2px 40px 0 rgba(0,0,0,0.18)',
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar + 50,
      }}>
      <Toolbar
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          margin: '0 auto',
          maxWidth: (theme) => theme.breakpoints.values.lg,
        }}>
        <Box
          sx={{
            flexShrink: 1,
            minWidth: 0,
            maxWidth: `calc(100% - ${isLoggedIn ? 100 : 50}px)`,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="h6"
              noWrap
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flexShrink: 1,
                fontSize: '1rem',
              }}>
              {viewTitle}
            </Typography>
            {isChapterCompleted && !isExerciseCorrect && <Check sx={{ flexShrink: 0 }} />}
            {isExerciseCorrect && <DoneAll color="success" sx={{ flexShrink: 0 }} />}
          </Box>
          <Breadcrumbs
            separator="›"
            aria-label="breadcrumb"
            sx={{
              display: { xs: 'none', sm: 'block' },
              color: 'inherit',
              fontSize: 12,
              cursor: 'default',
              '& a': { cursor: 'pointer' },
              '& > .MuiBreadcrumbs-separator': {
                mx: '4px',
              },
            }}>
            {breadcrumbs.map((breadcrumb, i) => (
              <Box key={breadcrumb.routeId} component="span">
                {breadcrumbs.length === i + 1 ? (
                  <Box component="span" sx={{ color: 'text.primary' }}>
                    {breadcrumb.title}
                  </Box>
                ) : (
                  <Link
                    color="inherit"
                    onClick={() => onNavigate(breadcrumb.routeId, breadcrumb.params)}>
                    {breadcrumb.title}
                  </Link>
                )}
              </Box>
            ))}
          </Breadcrumbs>
        </Box>

        <Box sx={{ display: 'none' }}>
          <IconButton
            aria-label="Go to start page"
            color="inherit"
            onClick={() => onNavigate('start')}
            size="large">
            <Badge badgeContent={4} color="secondary">
              <Mail />
            </Badge>
            Start
          </IconButton>
          <IconButton
            aria-label="Go to my courses"
            color="inherit"
            onClick={() => onNavigate('myCourses')}
            size="large">
            <Badge badgeContent={17} color="secondary">
              <Notifications />
            </Badge>
            Meine Kurse
          </IconButton>
          <IconButton
            edge="end"
            aria-label="account of current user"
            aria-haspopup="true"
            color="inherit"
            size="large">
            <AccountCircle />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex' }}>
          {isRoute('myCourseLearn') &&
            selectedCourse?.type === 'Course' &&
            selectedChapter &&
            isLoggedIn &&
            (() => {
              const isExerciseChapter = selectedChapter.type === 'Exercise';
              return (
                <Tooltip
                  title={
                    isExerciseChapter
                      ? t('learn.chapterNote.exerciseDisabledTooltip')
                      : t('learn.chapterNote.summaryTooltip')
                  }>
                  <span>
                    <IconButton
                      disableRipple
                      disabled={isExerciseChapter}
                      sx={{
                        backgroundColor:
                          showChapterNote && !isExerciseChapter ? 'action.selected' : '',
                        opacity: isExerciseChapter ? 0.38 : 1,
                      }}
                      onClick={toggleChapterNote}
                      aria-label="show chapter notes"
                      edge="start"
                      size="large">
                      <Description />
                    </IconButton>
                  </span>
                </Tooltip>
              );
            })()}
          <Tooltip
            title={t('test.menu.hint')}
            open={showTestMenuHint}
            placement="bottom"
            arrow
            disableFocusListener
            disableHoverListener
            disableTouchListener>
            <IconButton
              disableRipple
              sx={{
                backgroundColor: showMenuDrawer ? 'action.selected' : '',
              }}
              aria-label="show menu"
              aria-haspopup="true"
              onClick={handleMenuToggle}
              edge="end"
              size="large">
              <Menu />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
